/**
 * Header behaviour: mega-menu (hover + keyboard), mobile drawer with focus
 * trap, and drawer sub-accordions.
 */
class LJHeader extends HTMLElement {
  connectedCallback() {
    this.initMegaMenus();
    this.initDrawers();
    this.initDrawerSubs();
    this.initSearchTypewriter();
  }

  /**
   * Types each search term out character by character, holds it, deletes it,
   * then moves to the next — the reference's animated placeholder.
   */
  initSearchTypewriter() {
    /* Two copies of the pill exist -- one for the header bar, one for the
       mobile row -- so every target is driven, not just the first. */
    const targets = [...document.querySelectorAll('[data-search-typewriter]')];
    if (targets.length === 0) return;
    const target = { set textContent(v) { targets.forEach(t => { t.textContent = v; }); } };

    const terms = window.LJSearchTerms || [];
    if (terms.length === 0) return;

    // Respect reduced-motion: show the first term statically instead.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      target.textContent = terms[0];
      return;
    }

    const TYPE_MS = 110;
    const DELETE_MS = 55;
    const HOLD_MS = 1400;
    const NEXT_MS = 350;

    let termIndex = 0;
    let charIndex = 0;
    let deleting = false;

    const tick = () => {
      const term = terms[termIndex];

      if (!deleting) {
        charIndex += 1;
        target.textContent = term.slice(0, charIndex);

        if (charIndex === term.length) {
          deleting = true;
          this.typeTimer = setTimeout(tick, HOLD_MS);
          return;
        }
        this.typeTimer = setTimeout(tick, TYPE_MS);
        return;
      }

      charIndex -= 1;
      target.textContent = term.slice(0, charIndex);

      if (charIndex === 0) {
        deleting = false;
        termIndex = (termIndex + 1) % terms.length;
        this.typeTimer = setTimeout(tick, NEXT_MS);
        return;
      }
      this.typeTimer = setTimeout(tick, DELETE_MS);
    };

    tick();

    // Stop animating while the tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        clearTimeout(this.typeTimer);
      } else {
        clearTimeout(this.typeTimer);
        tick();
      }
    });
  }

  /* ---------------------------------------------------------------- mega */
  initMegaMenus() {
    this.querySelectorAll('[data-megamenu]').forEach((menu) => {
      const trigger = menu.querySelector('[data-megamenu-trigger]');
      const panel = menu.querySelector('[data-megamenu-panel]');
      if (!trigger || !panel) return;

      let closeTimer = null;

      const open = () => {
        clearTimeout(closeTimer);
        this.closeAllMegaMenus(menu);
        panel.dataset.open = 'true';
        trigger.setAttribute('aria-expanded', 'true');
      };

      // Small delay stops the panel flickering shut when the pointer
      // crosses the gap between the trigger and the panel.
      const close = (immediate = false) => {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => {
          panel.dataset.open = 'false';
          trigger.setAttribute('aria-expanded', 'false');
        }, immediate ? 0 : 150);
      };

      menu.addEventListener('mouseenter', open);
      menu.addEventListener('mouseleave', () => close());

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (panel.dataset.open === 'true') close(true);
        else open();
      });

      menu.addEventListener('focusin', open);
      menu.addEventListener('focusout', (e) => {
        if (!menu.contains(e.relatedTarget)) close(true);
      });

      panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          close(true);
          trigger.focus();
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAllMegaMenus();
    });
  }

  disconnectedCallback() {
    clearTimeout(this.typeTimer);
  }

  closeAllMegaMenus(except = null) {
    this.querySelectorAll('[data-megamenu]').forEach((menu) => {
      if (menu === except) return;
      const panel = menu.querySelector('[data-megamenu-panel]');
      const trigger = menu.querySelector('[data-megamenu-trigger]');
      if (panel) panel.dataset.open = 'false';
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  /* -------------------------------------------------------------- drawer */
  initDrawers() {
    this.querySelectorAll('[data-drawer-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const drawer = document.getElementById(btn.dataset.drawerOpen);
        if (!drawer) return;
        // The mobile menu has no close button of its own: the burger becomes
        // an X and closes it again, so this has to toggle rather than open.
        if (drawer.dataset.open === 'true') this.closeDrawer(drawer);
        else this.openDrawer(drawer, btn);
      });
    });

    this.querySelectorAll('[data-drawer]').forEach((drawer) => {
      drawer.querySelectorAll('[data-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeDrawer(drawer));
      });

      drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeDrawer(drawer);
        if (e.key === 'Tab') this.trapFocus(e, drawer);
      });
    });
  }

  openDrawer(drawer, trigger) {
    this.lastTrigger = trigger;

    /* The mobile menu opens BELOW the header rather than over it. Measuring
       the header group here beats hard-coding a height in CSS: the group is
       announcement + bar + search row, and which of those exist changes with
       the template and the breakpoint. */
    if (drawer.hasAttribute('data-drawer-below-header')) {
      const group = this.closest('.shopify-section-group-header-group') || this;
      const bottom = Math.round(group.getBoundingClientRect().bottom);
      drawer.style.top = Math.max(0, bottom) + 'px';
    }

    drawer.dataset.open = 'true';
    document.body.style.overflow = 'hidden';
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    const focusable = this.getFocusable(drawer);
    if (focusable.length) focusable[0].focus();
  }

  closeDrawer(drawer) {
    drawer.dataset.open = 'false';
    document.body.style.overflow = '';

    if (this.lastTrigger) {
      this.lastTrigger.setAttribute('aria-expanded', 'false');
      this.lastTrigger.focus();
      this.lastTrigger = null;
    }
  }

  getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }

  /** Keep Tab inside the open drawer. */
  trapFocus(event, drawer) {
    const focusable = this.getFocusable(drawer);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* --------------------------------------------------- drawer sub-menus */
  initDrawerSubs() {
    this.querySelectorAll('[data-drawer-sub-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;

        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        panel.dataset.open = isOpen ? 'false' : 'true';
      });
    });
  }
}

customElements.define('lj-header', LJHeader);
